import React, { useState, useEffect } from 'react';
import { Company } from '../types/billing';

interface CompanyManagerProps {
  companies: Company[];
  onSave: (company: Company) => void;
  onUpdate: (company: Company) => void;
  onDelete: (companyId: string) => void;
  onClose: () => void;
  editingCompany?: Company | null;
}

const CompanyManager: React.FC<CompanyManagerProps> = ({
  companies,
  onSave,
  onUpdate,
  onDelete,
  onClose,
  editingCompany
}) => {
  // Form state
  const [formData, setFormData] = useState<Company>({
    id: '',
    name: '',
    address: '',
    phone: '',
    email: '',
    gstn: '',
    state: '',
    stateCode: '',
    isDefault: false
  });

  // UI state
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errors, setErrors] = useState<string[]>([]);

  // Initialize form data when editingCompany prop changes
  useEffect(() => {
    if (editingCompany) {
      setFormData(editingCompany);
      setIsEditing(true);
    } else {
      resetForm();
    }
  }, [editingCompany]);

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      id: '',
      name: '',
      address: '',
      phone: '',
      email: '',
      gstn: '',
      state: '',
      stateCode: '',
      isDefault: false
    });
    setIsEditing(false);
    setErrors([]);
    setSuccessMessage('');
  };

  // Handle input changes
  const handleInputChange = (field: keyof Company, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  // Validate form data
  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.name.trim()) {
      newErrors.push('Company name is required');
    }

    if (!formData.address.trim()) {
      newErrors.push('Company address is required');
    }

    if (!formData.phone.trim()) {
      newErrors.push('Phone number is required');
    }

    // Email is optional, but if provided, it must be valid
    if (formData.email.trim() && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.push('Please enter a valid email address');
    }

    if (!formData.gstn.trim()) {
      newErrors.push('GSTN is required');
    }

    if (!formData.state.trim()) {
      newErrors.push('State is required');
    }

    if (!formData.stateCode.trim()) {
      newErrors.push('State code is required');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSuccessMessage('');

    try {
      if (isEditing) {
        await onUpdate(formData);
        setSuccessMessage('Company updated successfully!');
      } else {
        await onSave({
          ...formData,
          id: Date.now().toString()
        });
        setSuccessMessage('Company added successfully!');
      }

      // Reset form and close after success
      resetForm();
      setTimeout(() => {
        onClose();
      }, 1500);

    } catch (error) {
      console.error('Error saving company:', error);
      setErrors(['Failed to save company. Please try again.']);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle edit action
  const handleEdit = (company: Company) => {
    setFormData(company);
    setIsEditing(true);
    setErrors([]);
    setSuccessMessage('');
  };

  // Handle delete action
  const handleDelete = (companyId: string) => {
    const company = companies.find(c => c.id === companyId);
    if (company && window.confirm(`Are you sure you want to delete "${company.name}"?`)) {
      onDelete(companyId);
    }
  };

  // Handle set default action
  const handleSetDefault = (companyId: string) => {
    const updatedCompany = companies.find(c => c.id === companyId);
    if (updatedCompany) {
      onUpdate({
        ...updatedCompany,
        isDefault: true
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden animate-scaleIn border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Manage Companies</h2>
            <p className="text-sm text-gray-600 mt-1">
              {isEditing ? 'Edit company information' : 'Add new company or manage existing ones'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            title="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row h-full max-h-[calc(90vh-80px)]">
          {/* Form Section */}
          <div className="flex-1 p-6 border-r border-gray-200 overflow-y-auto">
            <div className="max-w-md mx-auto">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {isEditing ? 'Edit Company' : 'Add New Company'}
                </h3>
                <p className="text-sm text-gray-600">
                  Fill in the company details below. All fields marked with * are required.
                </p>
              </div>

              {/* Error Messages */}
              {errors.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-red-800">Please fix the following errors:</h4>
                      <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex">
                    <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <div className="ml-3">
                      <p className="text-sm font-medium text-green-800">{successMessage}</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Company Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="form-input"
                    placeholder="Enter company name"
                    required
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className="form-input resize-none"
                    rows={3}
                    placeholder="Enter complete address"
                    required
                  />
                </div>

                {/* Phone and Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="form-input"
                      placeholder="Enter phone number"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="form-input"
                      placeholder="Enter email address (optional)"
                    />
                  </div>
                </div>

                {/* GSTN and State */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      GSTN *
                    </label>
                    <input
                      type="text"
                      value={formData.gstn}
                      onChange={(e) => handleInputChange('gstn', e.target.value)}
                      className="form-input"
                      placeholder="Enter GST number"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State * 
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="form-input"
                      placeholder="Enter state name"
                      required
                    />
                  </div>
                </div>

                {/* State Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State Code *
                  </label>
                  <input
                    type="text"
                    value={formData.stateCode}
                    onChange={(e) => handleInputChange('stateCode', e.target.value)}
                    className="form-input"
                    placeholder="Enter state code"
                    required
                  />
                </div>

                {/* Default Company Checkbox */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isDefault"
                    checked={formData.isDefault}
                    onChange={(e) => handleInputChange('isDefault', e.target.checked)}
                    className="h-4 w-4 text-avis-blue focus:ring-avis-blue border-gray-300 rounded"
                  />
                  <label htmlFor="isDefault" className="ml-2 block text-sm text-gray-700">
                    Set as default company
                  </label>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-avis-blue to-avis-dark text-white rounded-xl 
                             hover:shadow-lg transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed 
                             disabled:transform-none transition-all duration-200 font-semibold shadow-md"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </span>
                    ) : (
                      isEditing ? 'Update Company' : 'Add Company'
                    )}
                  </button>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 
                               hover:shadow-md transform hover:-translate-y-0.5 transition-all duration-200 font-semibold"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* Companies List */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Existing Companies</h3>
              <p className="text-sm text-gray-600">
                {companies.length} {companies.length === 1 ? 'company' : 'companies'} found
              </p>
            </div>

            {companies.length === 0 ? (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <h4 className="mt-2 text-sm font-medium text-gray-900">No companies</h4>
                <p className="mt-1 text-sm text-gray-500">Get started by adding a new company.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {companies.map((company) => (
                  <div
                    key={company.id}
                    className="p-5 border-2 border-gray-200 rounded-xl hover:border-avis-blue hover:shadow-lg 
                             bg-white transition-all duration-300 hover:-translate-y-1 card-hover"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="font-semibold text-gray-900 truncate">{company.name}</h4>
                          {company.isDefault && (
                            <span className="px-2 py-1 text-xs bg-avis-blue text-white rounded-full flex-shrink-0">
                              Default
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1 line-clamp-2">{company.address}</p>
                        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                          <span className="flex items-center">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {company.phone}
                          </span>
                          {company.email && (
                            <span className="flex items-center">
                              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              {company.email}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">GSTN: {company.gstn}</p>
                      </div>
                      <div className="flex items-center space-x-1 ml-2">
                        <button
                          onClick={() => handleEdit(company)}
                          className="p-2 text-gray-400 hover:text-avis-blue hover:bg-avis-blue/10 rounded-md transition-colors"
                          title="Edit company"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {!company.isDefault && (
                          <button
                            onClick={() => handleSetDefault(company.id)}
                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors"
                            title="Set as default"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(company.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete company"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyManager;
